import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { CreateCampaignModal } from '@/components/CreateCampaignModal';
import { useAuthStore } from '@/stores/authStore';
import { canWriteCampaigns } from '@/lib/campaignAccess';

interface Campaign {
  id: string;
  name: string;
  description: string | null;
  status: string;
  startsAt: string | null;
  endsAt: string | null;
}

const STATUSES = ['DRAFT', 'ACTIVE', 'PAUSED', 'CLOSED'];

export function CampaignsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  // VYV-K7.2 — campaign writes are gated server-side to the elevated roles, so
  // read-only staff (reps, pre-sales, finance, agency owners) keep the list and
  // the detail hub but are not offered a create button that would 403.
  const role = useAuthStore((s) => s.user?.role);
  const canWrite = canWriteCampaigns(role);
  // Story 46.2 — clicking a campaign now navigates to the hub.
  // The legacy in-page detail panel is retained as a fallback for
  // any tests that still open it via setSelected, but the row click
  // takes the user to /campaigns/:id.
  // setSelected stays available for future modal-style edits but the
  // current UX routes to /campaigns/:id on row click.
  const [selected, _setSelected] = useState<Campaign | null>(null);
  // Search by name from the top bar. Filtered client-side against the
  // already-loaded array — most tenants have < 100 campaigns and
  // /campaigns returns all of them. If that size grows, move this to a
  // server-side ?q= query param.
  const [searchQuery, setSearchQuery] = useState('');
  const normalizedQuery = searchQuery.trim().toLowerCase();
  const filteredCampaigns = normalizedQuery.length === 0
    ? campaigns
    : campaigns.filter((c) =>
        c.name.toLowerCase().includes(normalizedQuery) ||
        (c.description ?? '').toLowerCase().includes(normalizedQuery),
      );

  const load = useCallback(async () => {
    try {
      const { data } = await apiClient.get<Campaign[]>('/campaigns');
      setCampaigns(data);
    } catch {
      setCampaigns([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-4">
      <CreateCampaignModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={load}
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">{t('nav.campaigns')}</h1>
        <div className="flex flex-1 items-center justify-end gap-2 sm:flex-none">
          <Input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('campaigns.search.placeholder')}
            aria-label={t('campaigns.search.aria')}
            className="w-full sm:w-64"
          />
          <Link to="/analytics/campaigns">
            <Button size="sm" variant="outline">
              View Analytics
            </Button>
          </Link>
          {canWrite && (
            <Button size="sm" onClick={() => setShowCreate(true)}>
              + Campaign
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-[18rem_1fr]">
        <ul className="divide-y rounded-lg border bg-card">
          {filteredCampaigns.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                onClick={() => navigate(`/campaigns/${c.id}`)}
                className="flex w-full items-center justify-between px-4 py-3 text-left transition-colors hover:bg-accent"
              >
                <div>
                  <div className="font-medium">{c.name}</div>
                  {c.description && (
                    <div className="text-xs text-muted-foreground line-clamp-1">
                      {c.description}
                    </div>
                  )}
                </div>
                <span
                  className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${
                    c.status === 'ACTIVE'
                      ? 'bg-emerald-900/40 text-emerald-200'
                      : c.status === 'PAUSED'
                        ? 'bg-amber-900/40 text-amber-200'
                        : c.status === 'CLOSED'
                          ? 'bg-muted text-foreground'
                          : 'bg-blue-900/40 text-blue-200'
                  }`}
                >
                  {c.status}
                </span>
              </button>
            </li>
          ))}
          {filteredCampaigns.length === 0 && campaigns.length === 0 && (
            <li className="px-4 py-6 text-center text-sm text-muted-foreground">
              {t('common.loading')}
            </li>
          )}
          {filteredCampaigns.length === 0 && campaigns.length > 0 && (
            <li className="px-4 py-6 text-center text-sm text-muted-foreground">
              {t('campaigns.search.empty', { query: searchQuery })}
            </li>
          )}
        </ul>

        <div className="rounded-lg border bg-card p-6">
          {!selected && (
            <p className="text-sm text-muted-foreground">
              Select a campaign to view details.
            </p>
          )}
          {selected && (
            <CampaignDetail
              campaign={selected}
              onUpdated={() => {
                load();
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function CampaignDetail({
  campaign,
  onUpdated,
}: {
  campaign: Campaign;
  onUpdated: () => void;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(campaign.name);
  const [description, setDescription] = useState(campaign.description ?? '');
  const [status, setStatus] = useState(campaign.status);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    setName(campaign.name);
    setDescription(campaign.description ?? '');
    setStatus(campaign.status);
    setEditing(false);
  }, [campaign]);

  async function save() {
    try {
      await apiClient.patch(`/campaigns/${campaign.id}`, {
        name: name.trim(),
        description: description.trim() || null,
        status,
      });
      setEditing(false);
      setToast('Saved');
      setTimeout(() => setToast(null), 2000);
      onUpdated();
    } catch {
      setToast('Failed to save');
    }
  }

  return (
    <>
      {toast && (
        <div className="mb-4 rounded-md border bg-accent px-3 py-2 text-sm">
          {toast}
        </div>
      )}
      {editing ? (
        <div className="space-y-3">
          <Input value={name} onChange={(e) => setName(e.target.value)} />
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full rounded-md border bg-transparent px-3 py-2 text-sm"
            rows={3}
          />
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="rounded-md border bg-transparent px-3 py-2 text-sm"
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <div className="flex gap-2">
            <Button size="sm" onClick={save}>{t('dashboard.actions.save')}</Button>
            <Button size="sm" variant="outline" onClick={() => setEditing(false)}>
              {t('dashboard.actions.cancel')}
            </Button>
          </div>
        </div>
      ) : (
        <div>
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-lg font-semibold">{campaign.name}</h2>
              {campaign.description && (
                <p className="mt-1 text-sm text-muted-foreground">
                  {campaign.description}
                </p>
              )}
            </div>
            <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
              Edit
            </Button>
          </div>
          <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
            <div>
              <dt className="text-xs font-medium text-muted-foreground">Status</dt>
              <dd>{campaign.status}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-muted-foreground">Period</dt>
              <dd>
                {campaign.startsAt ?? '—'} → {campaign.endsAt ?? '—'}
              </dd>
            </div>
          </dl>
        </div>
      )}
    </>
  );
}

// CreateCampaignModal is now in components/CreateCampaignModal.tsx — see
// that file for the full multi-section form (objective, channels, budget,
// owner, forecast targets, etc).
